import { supabase } from '../supabase';
import { formatDateInBusinessTZ } from '../../utils/date';

/** A bundle payment (payment cart) as the admin pages read it. */
export interface AdminCart {
  id: string;
  kind: 'card' | 'ach' | 'zelle';
  status: 'open' | 'partially_funded' | 'settled' | 'expired' | 'cancelled';
  charge_status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled' | null;
  memo_code: string | null;
  payment_intent_id: string | null;
  target_amount_cents: number;
  fee_cents: number;
  expires_at: string;
  created_at: string;
  parent: { id: string; first_name: string | null; last_name: string | null; email: string | null } | null;
  payment_cart_items: Array<{
    invoice_id: string;
    snapshot_balance_cents: number;
    sort_order: number;
    invoices: {
      total_amount: number;
      amount_paid: number;
      status: string;
      memo_code?: string | null;
      enrollments?: {
        students: { first_name: string; last_name: string } | null;
        programs: { name: string } | null;
      } | null;
    } | null;
  }>;
  payment_cart_fundings: Array<{
    id?: string;
    source_type?: string;
    source_id?: string;
    amount_cents: number;
    allocated_cents?: number;
    residue_cents: number;
    allocation_rule?: string;
    received_at?: string;
  }>;
  payments?: Array<{
    id: string;
    invoice_id: string;
    amount: number;
    refunded_amount: number | null;
    status: string;
    payment_method: string;
    paid_by: string | null;
  }>;
}

export const CART_LIST_SELECT = `
  id, kind, status, charge_status, memo_code, payment_intent_id, target_amount_cents, fee_cents, expires_at, created_at,
  parent:parents ( id, first_name, last_name, email ),
  payment_cart_items ( invoice_id, snapshot_balance_cents, sort_order, invoices ( total_amount, amount_paid, status ) ),
  payment_cart_fundings ( amount_cents, residue_cents )
`;

export const CART_DETAIL_SELECT = `
  id, kind, status, charge_status, memo_code, payment_intent_id, target_amount_cents, fee_cents, expires_at, created_at,
  parent:parents ( id, first_name, last_name, email ),
  payment_cart_items ( invoice_id, snapshot_balance_cents, sort_order,
    invoices ( total_amount, amount_paid, status, memo_code, enrollments ( students ( first_name, last_name ), programs ( name ) ) ) ),
  payment_cart_fundings ( id, source_type, source_id, amount_cents, allocated_cents, residue_cents, allocation_rule, received_at ),
  payments ( id, invoice_id, amount, refunded_amount, status, payment_method, paid_by )
`;

function cents(dollars: number | null | undefined): number {
  return Math.round(Number(dollars ?? 0) * 100);
}

/** Everything that has arrived against the cart, across every send. */
export function cartReceivedCents(cart: Pick<AdminCart, 'payment_cart_fundings'>): number {
  return (cart.payment_cart_fundings ?? []).reduce((sum, f) => sum + f.amount_cents, 0);
}

/** Of that, what went to the family's BHCS Cash because the invoices owed less. */
export function cartWalletCreditCents(cart: Pick<AdminCart, 'payment_cart_fundings'>): number {
  return (cart.payment_cart_fundings ?? []).reduce((sum, f) => sum + f.residue_cents, 0);
}

/** What the cart's invoices still owe now (live, not the snapshot). */
export function cartOwedCents(cart: Pick<AdminCart, 'payment_cart_items'>): number {
  return (cart.payment_cart_items ?? []).reduce((sum, item) => {
    const inv = item.invoices;
    if (!inv || inv.status === 'voided') return sum;
    return sum + Math.max(0, cents(inv.total_amount) - cents(inv.amount_paid));
  }, 0);
}

export function isLiveZelleCart(cart: Pick<AdminCart, 'kind' | 'status' | 'expires_at'>, now = Date.now()): boolean {
  return cart.kind === 'zelle' && (cart.status === 'open' || cart.status === 'partially_funded') && Date.parse(cart.expires_at) > now;
}

/** Where a cart stands, in the words an admin needs. */
export function cartStatusLabel(cart: Pick<AdminCart, 'kind' | 'status' | 'charge_status' | 'expires_at'>, now = Date.now()): string {
  if (cart.status === 'settled') return 'Paid';
  if (cart.kind === 'zelle') {
    if (cart.status === 'cancelled') return 'Replaced';
    if (!isLiveZelleCart(cart, now)) return cart.status === 'partially_funded' ? 'Expired, part-paid' : 'Expired, unpaid';
    const until = formatDateInBusinessTZ(cart.expires_at);
    return cart.status === 'partially_funded' ? `Part-paid, open until ${until}` : `Waiting for Zelle, until ${until}`;
  }
  switch (cart.charge_status) {
    case 'processing': return 'ACH processing';
    case 'failed': return 'Charge failed';
    case 'canceled': return 'Replaced or canceled';
    case 'pending': return 'Checkout not finished';
    default: return cart.status === 'partially_funded' ? 'Part-paid' : cart.status;
  }
}

export function payerName(cart: Pick<AdminCart, 'parent'>): string {
  const p = cart.parent;
  if (!p) return 'Unknown parent';
  return [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.email || 'Unknown parent';
}

/** What a payment row can still give back. */
export function refundableCents(payment: { amount: number; refunded_amount: number | null }): number {
  return Math.max(0, cents(payment.amount) - cents(payment.refunded_amount));
}

export interface WholeCartRefundResult {
  refunded: Array<{ paymentId: string; cents: number }>;
  failed: { paymentId: string; message: string } | null;
}

/**
 * Refund every invoice's remaining share of a card bundle payment, one process-refund call per
 * payment row, so each invoice is walked back on its own (Stripe refunds the shared charge in
 * parts). Stops at the first failure and says which: anything already refunded stays refunded,
 * and running it again only refunds what is left.
 */
export async function refundWholeCart(
  payments: Array<{ id: string; amount: number; refunded_amount: number | null; status: string }>,
  note: string,
): Promise<WholeCartRefundResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Authentication error: no valid session');
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refund`;

  const refunded: WholeCartRefundResult['refunded'] = [];
  for (const payment of payments) {
    const amount = refundableCents(payment);
    if (amount <= 0 || !['completed', 'partially_refunded'].includes(payment.status)) continue;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        paymentId: payment.id,
        amount,
        reason: 'requested_by_customer',
        manualRefundNotes: note,
        refundMethod: 'card',
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string };
      return { refunded, failed: { paymentId: payment.id, message: body.error ?? `HTTP ${response.status}` } };
    }
    refunded.push({ paymentId: payment.id, cents: amount });
  }
  return { refunded, failed: null };
}
